import {
  ArgumentAxis,
  BarSeries,
  Chart,
  Title,
  Tooltip,
  ValueAxis,
} from '@devexpress/dx-react-chart-material-ui'
import { ArgumentScale, EventTracker } from '@devexpress/dx-react-chart'
import { Box, Button, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { scaleBand } from '@devexpress/dx-chart-core'
import Paper from '@material-ui/core/Paper'
import React from 'react'
import moment from 'moment-timezone'

import goAdminApi from '@/libs/api/goAdmin'

// server 端以 Asia/Taipei (UTC+8) 切日；from/to 固定用 +8 送出，不受 client timezone 影響
// UI 語意：from 與 to 皆為 inclusive；送給 API 時 to 會 +1 天轉為 exclusive
const SERVER_TZ_OFFSET = '+08:00'
const MAX_RANGE_DAYS = 31

function today () {
  return moment.tz('Asia/Hong_Kong').format('YYYY-MM-DD')
}

function firstDayOfMonth () {
  return moment.tz('Asia/Hong_Kong').startOf('month').format('YYYY-MM-DD')
}

function RotatedLabel (props) {
  const { text, x, y } = props
  return (
    <text
      x={x}
      y={y + 8}
      dy='0.71em'
      textAnchor='end'
      transform={`rotate(-45, ${x}, ${y})`}
      style={{ fontSize: 11, fill: '#666' }}
    >
      {text}
    </text>
  )
}

function BarWithValueLabel (props) {
  const { arg, val, startVal, barWidth, maxBarWidth, color, value } = props
  const width = (barWidth || 0.9) * (maxBarWidth || 20)
  const height = Math.max(0, startVal - val)
  return (
    <g>
      <rect
        x={arg - width / 2}
        y={val}
        width={width}
        height={height}
        fill={color}
      />
      {value > 0 && (
        <text
          x={arg}
          y={val - 4}
          textAnchor='middle'
          style={{ fontSize: 11, fill: '#333' }}
        >
          {value}
        </text>
      )}
    </g>
  )
}

export default function PayQRDailyChart () {
  const classes = useStyles()
  const [from, setFrom] = React.useState(firstDayOfMonth())
  const [to, setTo] = React.useState(today())
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [queriedRange, setQueriedRange] = React.useState({ from: firstDayOfMonth(), to: today() })

  const fetchData = React.useCallback(async (fromDate, toDate) => {
    // 以 +08:00 固定送出，忽略 client timezone；UI 的 to 為 inclusive，送給 server 時 +1 天轉為 exclusive
    const fromISO = `${fromDate}T00:00:00${SERVER_TZ_OFFSET}`
    const toExclusive = moment(toDate).add(1, 'day').format('YYYY-MM-DD')
    const toISO = `${toExclusive}T00:00:00${SERVER_TZ_OFFSET}`

    try {
      setLoading(true)
      const response = await goAdminApi.getPayQRDailyCount({ from: fromISO, to: toISO })
      setData(response || [])
      setQueriedRange({ from: fromDate, to: toDate })
    } catch (err) {
      console.error('getPayQRDailyCount error', err)
      window.alert('載入失敗: ' + (err?.response?.data?.message || err?.message || err))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    fetchData(from, to)
  }, [])

  const handleSearch = () => {
    if (!moment(from, 'YYYY-MM-DD', true).isValid()) {
      window.alert('起始日期格式錯誤')
      return
    }
    if (!moment(to, 'YYYY-MM-DD', true).isValid()) {
      window.alert('結束日期格式錯誤')
      return
    }
    const diffDays = moment(to).diff(moment(from), 'days')
    if (diffDays < 0) {
      window.alert('結束日期必須大於或等於起始日期')
      return
    }
    if (diffDays + 1 > MAX_RANGE_DAYS) {
      window.alert(`查詢區間不可超過 ${MAX_RANGE_DAYS} 天`)
      return
    }
    fetchData(from, to)
  }

  const chartData = React.useMemo(() => {
    const days = []
    const start = moment(queriedRange.from)
    const end = moment(queriedRange.to)
    for (const d = start.clone(); d.isSameOrBefore(end, 'day'); d.add(1, 'day')) {
      days.push({
        date: d.format('YYYY-MM-DD'),
        label: d.format('DD/MM/YYYY'),
        count: 0,
      })
    }
    const byDate = {}
    for (const day of days) byDate[day.date] = day
    for (const entry of data) {
      if (byDate[entry.date]) byDate[entry.date].count = entry.count
    }
    return days
  }, [data, queriedRange])

  return (
    <Paper className={classes.paper}>
      <Box className={classes.controls}>
        <TextField
          label='From'
          type='date'
          size='small'
          variant='outlined'
          value={from}
          onChange={e => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label='To'
          type='date'
          size='small'
          variant='outlined'
          value={to}
          onChange={e => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant='contained'
          color='primary'
          onClick={handleSearch}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Search'}
        </Button>
      </Box>
      <Chart data={chartData}>
        <Title text={`每日 QR 付款訂單數 (${queriedRange.from} ~ ${queriedRange.to})`} />
        <ArgumentAxis labelComponent={RotatedLabel} />
        <ArgumentScale factory={scaleBand} />
        <ValueAxis />
        <BarSeries
          valueField='count'
          argumentField='label'
          name='QR 付款訂單數'
          pointComponent={BarWithValueLabel}
        />
        <EventTracker />
        <Tooltip />
      </Chart>
    </Paper>
  )
}

const useStyles = makeStyles(theme => ({
  paper: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
  },
}))
